<script setup>
import {onMounted} from "vue";

onMounted(() => {
    var container = document.getElementById('heroOrbit');
    var flowie = document.getElementById('flowie-hero');
    var sats = container.querySelectorAll('.orbit-sat');
    var heroSection = container.closest('.hero');
    var isMobile = window.innerWidth < 768;
    var isLg = window.innerWidth >= 1024;
    var radiusScale = isMobile ? 0.85 : isLg ? 1.1 : 1;

    var FINAL_SPEED = 0.00025;
    var INTRO_SPEED = FINAL_SPEED * 10;
    var FINAL_ELLIPSE = 0.45;
    var INTRO_ELLIPSE = 0.85;
    var FLAT_ELLIPSE = 1.0;
    var REPEL_RADIUS = 120;
    var REPEL_STRENGTH = 35;
    var LABEL_OFFSET = 56;

    var mouseX = -9999, mouseY = -9999;
    var startTime = performance.now();

    container.addEventListener('mousemove', function(e) {
        var rect = container.getBoundingClientRect();
        mouseX = e.clientX - rect.left - rect.width / 2;
        mouseY = e.clientY - rect.top - rect.height * 0.55;
    });
    container.addEventListener('mouseleave', function() {
        mouseX = -9999; mouseY = -9999;
    });

    var satData = Array.from(sats).map(function(sat, i) {
        var w = sat.offsetWidth;
        var h = sat.offsetHeight;
        sat.style.opacity = '0';

        var label = document.createElement('div');
        label.className = 'orbit-label';
        label.textContent = sat.dataset.role || '';
        container.appendChild(label);

        return {
            el: sat, index: i,
            label: label,
            halfW: w * 0.5, halfH: h * 0.5,
            startAngle: parseFloat(sat.dataset.angle) * Math.PI / 180,
            radius: parseFloat(sat.dataset.radius) * radiusScale,
            bobPhase: parseFloat(sat.dataset.bobPhase),
            bobAmp: parseFloat(sat.dataset.bobAmp),
            pushX: 0, pushY: 0,
            visible: false,
            angleAccum: 0,
            lastTick: 0,
        };
    });

    function easeOutBack(t) { var c = 1.4; return 1 + c * Math.pow(t - 1, 3) + (c - 1) * Math.pow(t - 1, 2); }
    function easeOutQuart(t) { return 1 - Math.pow(1 - t, 4); }
    function easeInOutCubic(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

    var FLOWIE_DUR = 1200;
    var SAT_DELAY = 400;
    var SETTLE_DUR = 4200;
    var UNFOLD_START = 1000;
    var UNFOLD_DUR = 3200;
    var LABEL_FADE_DUR = 700;
    var ORBIT_DROP_Y = 70;

    var STAGGER_GAP = Math.PI * 0.28;
    satData.forEach(function(s, i) {
        s.introAngle = -Math.PI / 2 - (satData.length - i) * STAGGER_GAP;
        s.angleAccum = s.introAngle;
        s.lastTick = 0;
    });

    var flowieDone = false;
    var heroVisible = true;
    var heroPauseTime = 0;

    var heroVisObs = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
            if (entry.isIntersecting) {
                if (!heroVisible) {
                    var gap = performance.now() - heroPauseTime;
                    startTime += gap;
                    satData.forEach(function(s) {
                        s.lastTick += gap;
                        if (s.visibleSince) s.visibleSince += gap;
                    });
                    heroVisible = true;
                    requestAnimationFrame(tick);
                }
            } else {
                heroVisible = false;
                heroPauseTime = performance.now();
            }
        });
    }, { threshold: 0 });
    heroVisObs.observe(heroSection);

    function tick(now) {
        if (!flowieDone) {
            var ft = Math.min((now - startTime) / FLOWIE_DUR, 1);
            var fe = easeOutBack(ft);
            flowie.style.opacity = Math.min(ft * 2, 1);
            flowie.style.transform = 'scale(' + fe + ')';
            if (ft >= 1) {
                flowieDone = true;
                flowie.style.animation = 'flowieBob 5s ease-in-out infinite';
            }
        }

        var globalStart = startTime + SAT_DELAY;
        var globalElapsed = Math.max(now - globalStart, 0);
        var settleT = Math.min(globalElapsed / SETTLE_DUR, 1);
        var settleEased = easeOutQuart(settleT);

        var unfoldElapsed = Math.max(globalElapsed - UNFOLD_START, 0);
        var unfoldT = Math.min(unfoldElapsed / UNFOLD_DUR, 1);
        var unfoldEased = easeInOutCubic(unfoldT);

        var labelElapsed = Math.max(unfoldElapsed - UNFOLD_DUR * 0.7, 0);
        var labelT = Math.min(labelElapsed / LABEL_FADE_DUR, 1);

        var curSpeed = INTRO_SPEED + (FINAL_SPEED - INTRO_SPEED) * settleEased;
        var settleEllipse = INTRO_ELLIPSE + (FINAL_ELLIPSE - INTRO_ELLIPSE) * settleEased;
        var curEllipse = settleEllipse + (FLAT_ELLIPSE - settleEllipse) * unfoldEased;
        var orbitDropY = ORBIT_DROP_Y * (1 - unfoldEased);

        for (var i = 0; i < satData.length; i++) {
            var s = satData[i];
            if (s.lastTick === 0) s.lastTick = now;
            var dt = now - s.lastTick;
            s.lastTick = now;

            if (globalElapsed <= 0) continue;

            s.angleAccum += curSpeed * dt;

            var targetAngle = s.startAngle + globalElapsed * FINAL_SPEED;
            var a = settleT < 1
                ? s.angleAccum + (targetAngle - s.angleAccum) * settleEased * settleEased
                : targetAngle;

            var sinA = Math.sin(a);
            var cosA = Math.cos(a);
            var r = s.radius;
            var orbitX = cosA * r;
            var orbitY = sinA * r * curEllipse;
            var bob = Math.sin(now * 0.002 + s.bobPhase) * s.bobAmp * settleEased * (1 - unfoldEased);

            var fullDepthScale = 0.75 + 0.25 * (sinA * 0.5 + 0.5);
            var fullDepthOpacity = 0.65 + 0.35 * (sinA * 0.5 + 0.5);
            var depthScale = fullDepthScale + (1 - fullDepthScale) * unfoldEased;
            var depthOpacity = fullDepthOpacity + (1 - fullDepthOpacity) * unfoldEased;

            var rawY = sinA * settleEllipse;
            if (!s.visible && rawY < -0.2) {
                s.visible = true;
                s.visibleSince = now;
            }

            if (!s.visible) {
                s.el.style.opacity = '0';
                if (s.label) s.label.style.opacity = '0';
                continue;
            }

            var timeSinceVisible = now - s.visibleSince;
            var fadeT = Math.min(timeSinceVisible / 400, 1);
            var scaleIn = 0.4 + 0.6 * easeOutBack(Math.min(fadeT * 1.2, 1));

            var finalX = orbitX;
            var finalY = orbitY + bob;

            if (settleT > 0.3) {
                var dx = finalX - mouseX;
                var dy = finalY - mouseY;
                var dist = Math.sqrt(dx * dx + dy * dy);
                var tpx = 0, tpy = 0;
                if (dist < REPEL_RADIUS && dist > 0) {
                    var force = (1 - dist / REPEL_RADIUS) * REPEL_STRENGTH * Math.min(settleT, 1);
                    tpx = (dx / dist) * force;
                    tpy = (dy / dist) * force;
                }
                s.pushX += (tpx - s.pushX) * 0.08;
                s.pushY += (tpy - s.pushY) * 0.08;
                finalX += s.pushX;
                finalY += s.pushY;
            }

            var tx = finalX - s.halfW;
            var ty2 = finalY - s.halfH + orbitDropY;
            s.el.style.transform = 'translate3d(' + tx + 'px,' + ty2 + 'px,0) scale(' + (depthScale * scaleIn) + ')';
            s.el.style.opacity = depthOpacity * fadeT;

            if (s.label) {
                if (labelT > 0) {
                    var outR = r + LABEL_OFFSET;
                    var labelX = cosA * outR + s.pushX;
                    var labelY = sinA * outR * curEllipse + s.pushY + orbitDropY;
                    s.label.style.transform = 'translate3d(' + labelX + 'px,' + labelY + 'px,0) translate(-50%,-50%)';
                    s.label.style.opacity = labelT * fadeT;
                } else {
                    s.label.style.opacity = '0';
                }
            }
        }

        if (heroVisible) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
})

</script>

<template>
    <div class="hero">
        <div class="orbit-wrapper" id="heroOrbit">
            <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/flowie-wizard.webp" alt="Flowie" id="flowie-hero">
            <div
                class="orbit-sat"
                data-angle="0"
                data-radius="160"
                data-bob-phase="0"
                data-bob-amp="5"
                data-role="Web Designer"
            >
<!--                <ai-agent-web-designer-avatar class="w-full h-full" />-->
                <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/paige-orbit.webp"  alt="Scout">
            </div>
            <div
                class="orbit-sat"
                data-angle="45"
                data-radius="160"
                data-bob-phase="0.8"
                data-bob-amp="6"
                data-role="Lead Finder"
            >
                <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/scout-orbit.webp"  alt="Scout">
            </div>
            <div
                class="orbit-sat"
                data-angle="90"
                data-radius="160"
                data-bob-phase="1.6"
                data-bob-amp="5"
                data-role="Email Specialist"
            >
                <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/reach-orbit.webp"  alt="Reach">
            </div>
            <div
                class="orbit-sat"
                data-angle="135"
                data-radius="160"
                data-bob-phase="2.4"
                data-bob-amp="7"
                data-role="Content Creator"
            >
                <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/spark-orbit.webp"  alt="Spark">
            </div>
            <div
                class="orbit-sat"
                data-angle="180"
                data-radius="160"
                data-bob-phase="3.2"
                data-bob-amp="5"
                data-role="Data Analyst"
            >
                <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/pulse-orbit.webp"  alt="Pulse">
            </div>
            <div
                class="orbit-sat"
                data-angle="225"
                data-radius="160"
                data-bob-phase="4.0"
                data-bob-amp="6"
                data-role="Relationship Manager"
            >
                <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/buddy-orbit.webp"  alt="Buddy">
            </div>
            <div
                class="orbit-sat"
                data-angle="270"
                data-radius="160"
                data-bob-phase="4.8"
                data-bob-amp="5"
                data-role="Sales Assistant"
            >
                <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/closer-orbit.webp" alt="Closer">
            </div>
            <div
                class="orbit-sat"
                data-angle="315"
                data-radius="160"
                data-bob-phase="5.6"
                data-bob-amp="7"
                data-role="Automation Expert"
            >
                <img src="https://myflow.fra1.digitaloceanspaces.com/prereg/ai-teammates/loop-orbit.webp"   alt="Loop">
            </div>
        </div>
    </div>
</template>

<style scoped>
* { box-sizing: border-box; }
html, body {
    margin: 0;
    padding: 0;
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: #fafafa;
    color: #1a1a1a;
}

.hero {
    display: flex;
    align-items: center;
    justify-content: center;
}

.orbit-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 100%;
    max-width: 640px;
    height: 520px;
    contain: layout style;
    will-change: transform;
}

#flowie-hero {
    position: relative;
    z-index: 10;
    width: 11rem;
    height: 11rem;
    object-fit: contain;
    opacity: 0;
    transform: scale(0);
}

@media (min-width: 768px) {
    #flowie-hero { width: 15rem; height: 15rem; }
}

@media (min-width: 1024px) {
    #flowie-hero { width: 17rem; height: 17rem; }
}

.orbit-sat {
    position: absolute;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    will-change: transform, opacity;
    backface-visibility: hidden;
    contain: layout style paint;
    z-index: 5;
}

.orbit-sat img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

@media (min-width: 768px) {
    .orbit-sat { width: 72px; height: 72px; }
}

@media (min-width: 1024px) {
    .orbit-sat { width: 80px; height: 80px; }
}

@keyframes flowieBob {
    0%, 100% { transform: translate3d(0,0,0); }
    50% { transform: translate3d(0,-10px,0); }
}
</style>
<style>
#heroOrbit .orbit-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: #1a1a1a;
    background: rgba(255,255,255,0.92);
    padding: 4px 10px;
    border-radius: 999px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    z-index: 6;
    will-change: transform, opacity;
    backface-visibility: hidden;
}

@media (min-width: 768px) {
    #heroOrbit .orbit-label { font-size: 14px; padding: 5px 12px; }
}
</style>
